/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  Button,
  Checkbox,
  Conditional,
  Popup,
  Space,
  Text,
} from '@cashfree-intl/coherent';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import { Form } from 'semantic-ui-react';
import request from 'utils/request';

import {
  captureBlur,
  generateReCAPTCHA,
  getClassByCharacters,
  getDeviceId,
  isReferralFlow,
  preventPasteEvent,
  sentryCaptureException,
  useQuery,
} from 'utils/common';
import { DEFAULT_CHARACTERS_CLASS, PARTNER } from 'utils/constants';

import EVENTS from 'constants/analytics';
import Analytics from 'utils/analytics';

import CustomIcon from 'components/CustomIcons';
import FormInput from 'components/FormInput';
import FormikComponent from 'components/Formik';
import TncModal from 'components/TncModal';
import { showToast } from 'containers/Toast/actions';
import {
  PARTNER_TERMS_URL,
  RECAPTCHA_FAILED_MESSAGE,
  SIGNUP_TYPE,
} from '../../OnboardingContainer/constants';

import { trackSignupEvent } from '../helper';
import {
  userDetailsValidate as validate,
  userDetailsSchema as validationSchema,
} from '../validation';

export function UserDetailsForm(props) {
  const [showEyeIcon, toggleEyeIcon] = useState(false);
  const [showReferralCode, resetReferralCodeDisplay] = useState(false);
  const [showTnc, setShowTnc] = useState(false);
  const [partnerDetail, setPartnerDetails] = useState({});

  const {
    userDetails,
    referrer,
    enableLoader,
    onContinue,
    disableLoader,
  } = props;

  const query = useQuery();

  const sessionEmail = sessionStorage.getItem('email');
  const sessionIdentity = sessionStorage.getItem('identity');

  useEffect(() => {
    if (referrer === PARTNER && query.get('refCode')) {
      (async () => {
        const { url, method } = PARTNER_TERMS_URL;
        const updatedURL = url.replace('$refCode$', query.get('refCode'));
        try {
          const response = await request(updatedURL, { method });
          setPartnerDetails(response);
        } catch (e) {
          showToast(e);
        }
      })();
    }
  }, []);

  useEffect(() => {
    if (sessionEmail && sessionIdentity) {
      submitUserDetails({ email: sessionEmail, password: sessionIdentity });
    }
  }, [sessionEmail, sessionIdentity]);

  let initialValues = isReferralFlow(referrer.toUpperCase())
    ? { ...userDetails, refCode: query.get('refCode') || '' }
    : userDetails;

  initialValues =
    referrer === PARTNER
      ? { ...initialValues, merchantConsent: '1' }
      : initialValues;

  const showReferalInput = showReferralCode || !!initialValues.refCode;

  const showPartnerConsent =
    referrer === PARTNER && partnerDetail.isConsentRequired;

  const submitUserDetailsForm = async (values, actions, captchaResponse) => {
    Analytics.trackWithSDK(EVENTS.SIGNUP.STEP_1.CREATE_ACCOUNT, null);

    onContinue({
      ...values,
      channel: referrer,
      deviceId: await getDeviceId(),
      captchaResponse: captchaResponse || ' ',
      signupType: SIGNUP_TYPE.PASSWORD,
      ...(showPartnerConsent &&
        query.get('refCode') &&
        partnerDetail.termsId && { termsId: partnerDetail.termsId }),
      refCode: values.refCode || query.get('refCode') || '',
    });
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('identity');
    if (actions && actions.setSubmitting) {
      actions.setSubmitting(false);
    }
  };

  const submitUserDetails = (values, actions) => {
    enableLoader();
    const envObj = process.env;
    const isGamma = envObj.NODE_ENV === 'gamma';
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const byPassReCaptcha = useQuery().get('recpt') === '0';
    const onSubmitReCaptcha = async captchaResponse => {
      await submitUserDetailsForm(values, actions, captchaResponse);
    };

    if (isGamma || byPassReCaptcha) {
      submitUserDetailsForm(values, actions);
    } else {
      generateReCAPTCHA({
        onSuccess: onSubmitReCaptcha,
        onError: () => {
          onSubmitReCaptcha(' ');
          trackSignupEvent({
            action: 'validate_email',
            status: 'error',
            message: RECAPTCHA_FAILED_MESSAGE,
            signupType: SIGNUP_TYPE.PASSWORD,
          });
          disableLoader();
          if (actions && actions.setSubmitting) {
            actions.setSubmitting(false);
          }
          sentryCaptureException({
            errMsg: new Error(
              `${SIGNUP_TYPE.PASSWORD}: Failed to load recaptcha`,
            ),
            priority: 'p0',
            label: 'auth',
          });
        },
      });
    }
  };

  const handleEyeClick = () => {
    if (!showEyeIcon) {
      Analytics.trackWithSDK(EVENTS.SIGNUP.STEP_1.VIEW_PASSWORD, { referrer });
    }

    toggleEyeIcon(!showEyeIcon);
  };

  const toggleReferralDisplay = event => {
    event.preventDefault();
    Analytics.trackWithSDK(EVENTS.STEP_1.TOGGLE_REFERRAL_CODE, null);
    resetReferralCodeDisplay(!showReferralCode);
  };

  const getHintComponent = className => (
    <Text
      variant="b12"
      color="bodyLight"
      className={`password-hint ${
        showReferalInput ? 'mr-btm-16' : 'mr-btm-8'
      } mr-top-n8`}
    >
      Your password must have{' '}
      <span className={className.character}>8 to 20 characters</span>,{' '}
      <span className={className.alphabet}>1 alphabet</span>,{' '}
      <span className={className.number}>1 number</span>, and{' '}
      <span className={className.symbol}>1 symbol (@,#,$,%,^,&,+,.,=)</span>.
    </Text>
  );

  return (
    <FormikComponent
      initialValues={{
        ...initialValues,
        email: initialValues.email || sessionEmail,
        password: initialValues.password || sessionIdentity,
      }}
      validationSchema={validationSchema}
      validate={validate}
      onSubmit={submitUserDetails}
    >
      {({
        values = {},
        handleFieldBlur,
        handleFieldChange,
        handleSubmit,
        errors,
        touched,
        status = { show_password_hint: false },
        setStatus,
        setFieldValue,
        ...formikProps
      }) => {
        const disabledField =
          (!formikProps.isValidating && formikProps.isSubmitting) ||
          (showPartnerConsent && !+values.merchantConsent);

        let HintComponent = getHintComponent(DEFAULT_CHARACTERS_CLASS);

        if (
          document.activeElement.name === 'password' &&
          document.activeElement.value
        ) {
          const className = getClassByCharacters(document.activeElement.value);
          HintComponent = getHintComponent(className);
        }

        return (
          <Form autoComplete="off" onSubmit={handleSubmit}>
            <FormInput
              label="Business Email Address"
              name="email"
              value={values.email}
              placeholder="Rahulsharma@xyzcompany.com"
              errors={errors}
              touched={touched}
              onChange={handleFieldChange}
              onBlur={(e, data) =>
                captureBlur(e, data, handleFieldBlur, () =>
                  Analytics.trackWithSDK(EVENTS.SIGNUP.STEP_1.EMAIL, {
                    referrer,
                    actionType: 'input',
                  }),
                )
              }
            />
            <FormInput
              label="Set Password"
              name="password"
              type={showEyeIcon ? 'text' : 'password'}
              value={values.password}
              placeholder="Password"
              errors={errors}
              touched={touched}
              onChange={handleFieldChange}
              onBlur={(e, data) =>
                captureBlur(e, data, handleFieldBlur, () =>
                  Analytics.trackWithSDK(EVENTS.SIGNUP.STEP_1.PASSWORD, {
                    referrer,
                    actionType: 'input',
                  }),
                )
              }
              onFocus={() => setStatus({ show_password_hint: true })}
              onPaste={preventPasteEvent}
              icon={
                <Space alignItems="center" className="icon-wrapper">
                  <CustomIcon
                    componentName={showEyeIcon ? 'EYE_SLASH' : 'EYE'}
                    onClick={handleEyeClick}
                  />
                </Space>
              }
            />
            <Conditional if={status.show_password_hint}>
              {HintComponent}
            </Conditional>
            <Conditional if={showPartnerConsent}>
              <Checkbox
                name="merchantConsent"
                label={
                  <label htmlFor="consent">
                    <Text variant="b12" color="bodyLight">
                      {`I consent to allow ${
                        partnerDetail.partnerName
                      } to access my Cashfree Payments
                      account.`}{' '}
                      <span
                        className="tnc pointer"
                        onClick={() => setShowTnc(true)}
                      >
                        View Terms and Conditions
                      </span>
                    </Text>
                    <Popup
                      content={`${
                        partnerDetail.partnerName
                      } can see your account data such as payment and payout history. They can also create payments and take actions on your behalf.`}
                      trigger={
                        <span className="pointer">
                          <CustomIcon componentName="INFO" />
                        </span>
                      }
                      position="top left"
                    />
                  </label>
                }
                className="custom-checkbox whatsapp-opt-in mb-1"
                checked={!!+values.merchantConsent}
                onChange={(e, data) =>
                  setFieldValue('merchantConsent', `${+data.checked}`)
                }
              />
              <Space className="warning-note">
                <Text variant="b12" color="warning">
                  {`You can choose to deny account access for ${
                    partnerDetail.partnerName
                  } at anytime`}
                </Text>
              </Space>
            </Conditional>
            <Conditional if={isReferralFlow(referrer)}>
              {showReferalInput ? (
                <FormInput
                  label="Referral Code"
                  name="refCode"
                  value={values.refCode}
                  errors={errors}
                  touched={touched}
                  onChange={handleFieldChange}
                  onBlur={(e, data) =>
                    captureBlur(e, data, handleFieldBlur, () =>
                      Analytics.trackWithSDK(
                        EVENTS.SIGNUP.STEP_1.REFERRAL_CODE,
                        { actionType: 'input' },
                      ),
                    )
                  }
                  disabled={!!initialValues.refCode}
                />
              ) : (
                <Text
                  variant="b12"
                  color="primary"
                  className="mr-btm-8 pointer"
                  onClick={toggleReferralDisplay}
                >
                  Have a referral code?
                </Text>
              )}
            </Conditional>
            <Button
              id="recaptcha"
              fluid
              primary
              type="submit"
              disabled={disabledField}
              className={showReferalInput ? 'mr-top-16' : 'mr-top-24'}
            >
              Continue
            </Button>
            <TncModal
              open={showTnc}
              htmlContent={partnerDetail && partnerDetail.terms}
              buttonText="Close"
              handleOnAgree={() => setShowTnc(false)}
              closeModal={() => setShowTnc(false)}
            />
          </Form>
        );
      }}
    </FormikComponent>
  );
}

UserDetailsForm.propTypes = {
  userDetails: PropTypes.object.isRequired,
  referrer: PropTypes.string.isRequired,
  enableLoader: PropTypes.func.isRequired,
  disableLoader: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default memo(UserDetailsForm);
